/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return */
/* TODO: Add proper typing for API responses to remove these eslint-disable comments */

import { shallowRef } from 'vue'
import { getSuggestionsMachine } from '~/apps/search-wizard/inversify.config'
import { SuggestionsMachineInterpret } from '~/apps/search-wizard/campsites-search/location/machine/suggestionsMachine'
import { LocationWizardData } from '~/apps/search-wizard/domain/LocationWizardData'
import { waitForInterpreterState } from '~/utility/xstate/waitForInterpreterState'

export const useLocationSuggestionsStore = defineStore(
  'locationSuggestionsStore',
  () => {
    const stateStrings = ref<Array<string>>([])
    // Use shallowRef for XState context - replaced wholesale on each transition
    // This avoids Vue deeply tracking all suggestion properties (reduces reactivity overhead)
    const context = shallowRef<
      SuggestionsMachineInterpret['state']['context'] | null
    >(null)

    const suggestionsMachine = getSuggestionsMachine(
      (useRoute().params.lang as string) || 'en-gb',
    )

    suggestionsMachine.onTransition((state) => {
      stateStrings.value = state.toStrings()
      context.value = state.context
    })

    const input = computed(() => context?.value?.input)
    const suggestions = computed(() => context?.value?.suggestions)

    /**
     * Fetch location suggestions for the given input text
     *
     * @param input - Search text to get suggestions for
     * @returns Promise resolving to suggestions array (may be empty on error)
     *
     * **Error Handling:** Catches all errors and logs them. Returns suggestions
     * anyway for graceful degradation - UI shows whatever suggestions are available
     * even if the machine entered an error state.
     */
    async function getSuggestionsFor(input) {
      suggestionsMachine.send({
        type: 'SET_INPUT',
        input,
      })
      try {
        await waitForInterpreterState({
          machine: suggestionsMachine,
          successStates: ['idle'],
          errorStates: ['error'],
        })
      } catch (error) {
        console.error('getSuggestionsFor: Failed to get suggestions', error)
        // Return suggestions anyway for graceful degradation
      }
      return suggestions
    }

    /**
     * Wait for the suggestions machine to reach idle state
     *
     * @returns Promise resolving to the machine state when idle
     * @throws {Error} Re-throws machine errors after logging. Caller must handle.
     *
     * **Error Handling:** Does NOT catch errors gracefully - propagates them to caller.
     * Use this when you need to know if the machine failed (e.g., before critical operations).
     */
    async function waitForMachineIdle() {
      try {
        return await waitForInterpreterState({
          machine: suggestionsMachine,
          successStates: ['idle'],
          errorStates: ['error'],
        })
      } catch (error) {
        console.error('waitForMachineIdle: Machine entered error state', error)
        throw error
      }
    }

    /**
     * Select the first available location suggestion (best-effort)
     *
     * @returns Promise resolving to the first mapped suggestion, or undefined if none available
     *
     * **Error Handling:** Catches machine errors and continues with best-effort selection.
     * Always returns the first suggestion even if machine didn't reach idle state cleanly.
     * Use when automatic location selection is preferred over error propagation.
     */
    async function dispatchAsyncLocationSelection() {
      try {
        await waitForMachineIdle()
      } catch (error) {
        console.error(
          'dispatchAsyncLocationSelection: Failed to wait for machine idle',
          error,
        )
        // Continue with best-effort location selection
      }
      const suggestions = getMappedSuggestions(context.value?.suggestions)
      return suggestions[0]
    }

    function getMappedSuggestions(suggestions): LocationWizardData[] {
      let suggestionsFormatted: LocationWizardData[] = []
      if (suggestions.hierarchies) {
        suggestionsFormatted = suggestionsFormatted.concat(
          suggestions.hierarchies.map((suggestion) => {
            return { ...suggestion, ...{ dataType: 'hierarchy' } }
          }),
        )
      }
      if (suggestions.campsites) {
        suggestionsFormatted = suggestionsFormatted.concat(
          suggestions.campsites.map((suggestion) => {
            return { ...suggestion, ...{ dataType: 'campsite' } }
          }),
        )
      }
      if (suggestions.custom) {
        suggestionsFormatted = suggestionsFormatted.concat(
          suggestions.custom.map((suggestion) => {
            return { ...suggestion, ...{ dataType: 'custom' } }
          }),
        )
      }
      if (suggestions.recentSearches) {
        suggestionsFormatted = suggestionsFormatted.concat(
          suggestions.recentSearches.map((suggestion) => {
            return { ...suggestion, ...{ dataType: 'recentSearch' } }
          }),
        )
      }
      return suggestionsFormatted
    }

    /**
     * Load geographic coordinates for a custom location from Mapbox
     *
     * @param location - Custom location object with mapboxId
     * @returns Promise resolving to suggestion with loaded geo point
     * @throws {Error} Throws if machine fails or suggestion not found after loading
     *
     * **Error Handling:** Propagates errors to caller after wrapping with context.
     * Machine errors and missing suggestions both result in thrown errors.
     * Critical for search flows - caller must handle failures appropriately.
     */
    async function loadGeoPointForLocation(
      location: Extract<LocationWizardData, { dataType: 'custom' }>,
    ) {
      suggestionsMachine.send({
        type: 'LOAD_POINT_FOR_CUSTOM',
        mapboxId: location.mapboxId,
      })

      try {
        await waitForMachineIdle()
      } catch (error) {
        console.error(
          'loadGeoPointForLocation: Machine entered error state',
          error,
        )
        throw new Error(
          `Failed to load geo point for location: ${location.mapboxId} - ${String(error)}`,
        )
      }

      const suggestionWithPoint = context.value?.suggestions?.custom?.find(
        (suggestion) => suggestion.mapboxId === location.mapboxId,
      )

      if (!suggestionWithPoint) {
        throw new Error(
          'useLocationSuggestionsStore loadGeoPointForLocation: suggestionWithPoint not found',
        )
      }

      return suggestionWithPoint
    }

    return {
      context,
      input,
      suggestions,
      suggestionsMachine,
      stateStrings,
      waitForMachineIdle,
      getSuggestionsFor,
      dispatchAsyncLocationSelection,
      loadGeoPointForLocation,
      send: suggestionsMachine.send,
    }
  },
)
